import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Languages } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import StudentLevelsDialog, { StudentLevelBadges } from "./StudentLevelsDialog";

const inputCls = "w-full px-4 py-2.5 bg-white border border-[var(--parchment-2)] focus:border-[var(--maroon)] outline-none text-sm";

function StudentAddForm({ form, setForm, onAdd }) {
  return (
    <div className="mt-4 flex flex-wrap items-end gap-2">
      <div className="flex-1 min-w-[120px]">
        <label className="eyebrow block mb-1">Prénom</label>
        <input className={inputCls} value={form.first_name} onChange={(e) => setForm((s) => ({ ...s, first_name: e.target.value }))} data-testid="student-firstname-input" />
      </div>
      <div className="flex-1 min-w-[120px]">
        <label className="eyebrow block mb-1">Nom</label>
        <input className={inputCls} value={form.last_name} onChange={(e) => setForm((s) => ({ ...s, last_name: e.target.value }))} data-testid="student-lastname-input" />
      </div>
      <button className="btn-primary !py-2.5 !px-4 text-sm flex items-center gap-1.5" onClick={onAdd} data-testid="add-student-btn">
        <Plus size={14} /> Ajouter
      </button>
    </div>
  );
}

function StudentMgmtRow({ s, index, langDefs, confirmDelete, onEdit, onEditLevels, onAskDelete, onConfirmDelete }) {
  return (
    <div className="py-2.5 flex items-center justify-between gap-3" data-testid={`student-mgmt-row-${s.id}`}>
      <div className="flex items-center gap-3">
        <span className="font-mono-spec text-xs text-[var(--muted)]">{String(index + 1).padStart(2, "0")}</span>
        <span className="font-medium text-sm">{s.last_name.toUpperCase()} {s.first_name}</span>
        <StudentLevelBadges student={s} langDefs={langDefs} />
      </div>
      <div className="flex items-center gap-1.5">
        <button className="btn-secondary !py-1 !px-2 text-xs flex items-center gap-1" onClick={onEditLevels} title="Niveaux de langues" data-testid={`edit-levels-${s.id}`}>
          <Languages size={13} />
        </button>
        <button className="btn-secondary !py-1 !px-2 text-xs" onClick={onEdit} data-testid={`edit-student-${s.id}`}>
          <Pencil size={13} />
        </button>
        {confirmDelete ? (
          <button className="!py-1 !px-2 text-xs bg-[var(--late)] text-white" onClick={onConfirmDelete} data-testid={`confirm-delete-student-${s.id}`}>
            Confirmer ?
          </button>
        ) : (
          <button className="btn-secondary !py-1 !px-2 text-xs !text-[var(--late)]" onClick={onAskDelete} data-testid={`delete-student-${s.id}`}>
            <Trash2 size={13} />
          </button>
        )}
      </div>
    </div>
  );
}

function StudentEditDialog({ editDialog, setEditDialog, onSave }) {
  return (
    <Dialog open={!!editDialog} onOpenChange={(open) => !open && setEditDialog(null)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">Modifier l'élève</DialogTitle>
        </DialogHeader>
        {editDialog && (
          <div className="space-y-3">
            <div>
              <label className="eyebrow block mb-1">Prénom</label>
              <input className={inputCls} value={editDialog.first_name} onChange={(e) => setEditDialog((s) => ({ ...s, first_name: e.target.value }))} data-testid="edit-student-firstname" />
            </div>
            <div>
              <label className="eyebrow block mb-1">Nom</label>
              <input className={inputCls} value={editDialog.last_name} onChange={(e) => setEditDialog((s) => ({ ...s, last_name: e.target.value }))} data-testid="edit-student-lastname" />
            </div>
          </div>
        )}
        <DialogFooter>
          <button className="btn-secondary" onClick={() => setEditDialog(null)}>Annuler</button>
          <button className="btn-primary" onClick={onSave} data-testid="student-save-btn">Enregistrer</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function useStudentsData(classId) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [langDefs, setLangDefs] = useState([]);

  useEffect(() => {
    api.get("/language-levels").then(({ data }) => setLangDefs(data));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get(`/classes/${classId}/students`);
    setStudents(data);
    setLoading(false);
  }, [classId]);

  useEffect(() => {
    load();
  }, [load]);

  return { students, loading, langDefs, load };
}

function useStudentActions(classId, load, onChanged) {
  const [form, setForm] = useState({ first_name: "", last_name: "" });
  const [editDialog, setEditDialog] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const add = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error("Prénom et nom requis");
      return;
    }
    try {
      await api.post("/admin/students", { class_id: classId, ...form });
      setForm({ first_name: "", last_name: "" });
      toast.success("Élève ajouté");
      await load();
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/students/${editDialog.id}`, { first_name: editDialog.first_name, last_name: editDialog.last_name });
      setEditDialog(null);
      toast.success("Élève modifié");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/students/${id}`);
      setConfirmId(null);
      toast.success("Élève supprimé");
      await load();
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return { form, setForm, editDialog, setEditDialog, confirmId, setConfirmId, add, saveEdit, remove };
}

export default function StudentsPanel({ classId, klass, onChanged }) {
  const { students, loading, langDefs, load } = useStudentsData(classId);
  const { form, setForm, editDialog, setEditDialog, confirmId, setConfirmId, add, saveEdit, remove } = useStudentActions(classId, load, onChanged);
  const [levelsDialog, setLevelsDialog] = useState(null);

  return (
    <div className="bg-white border border-[var(--parchment-2)] p-6" data-testid="students-panel">
      <div className="pb-4 border-b border-[var(--parchment-2)]">
        <div className="eyebrow">Élèves · {klass?.name}</div>
        <h2 className="font-serif-display text-2xl font-semibold mt-1">{students.length} élève{students.length > 1 ? "s" : ""}</h2>
      </div>

      <StudentAddForm form={form} setForm={setForm} onAdd={add} />

      {loading ? (
        <div className="mt-4 text-[var(--ink-soft)]">Chargement...</div>
      ) : (
        <div className="mt-4 divide-y divide-[var(--parchment-2)]" data-testid="students-mgmt-list">
          {students.map((s, i) => (
            <StudentMgmtRow
              key={s.id}
              s={s}
              index={i}
              langDefs={langDefs}
              confirmDelete={confirmId === s.id}
              onEdit={() => setEditDialog({ ...s })}
              onEditLevels={() => setLevelsDialog(s)}
              onAskDelete={() => setConfirmId(s.id)}
              onConfirmDelete={() => remove(s.id)}
            />
          ))}
          {students.length === 0 && <div className="py-4 text-sm text-[var(--ink-soft)]">Aucun élève dans cette classe.</div>}
        </div>
      )}

      <StudentEditDialog editDialog={editDialog} setEditDialog={setEditDialog} onSave={saveEdit} />
      {levelsDialog && (
        <StudentLevelsDialog
          student={levelsDialog}
          langDefs={langDefs}
          onClose={() => setLevelsDialog(null)}
          onSaved={async () => {
            setLevelsDialog(null);
            await load();
          }}
        />
      )}
    </div>
  );
}
